/**
 * The legal reading surface, made live (#576 L25).
 *
 * `routeTable.js:76` routes `/legal`, but `App.jsx` had no `LIVE_CONTAINERS` entry for it, so the
 * page rendered inside the fixture gallery. Measured on dev `7e25d6c`: the page showed the REAL
 * version (`v1.0.1`) and all 7 sections, and carried the gallery's persistent notice — *"Nothing
 * here can be saved"* — across the top of the family's actual Privacy Policy. A preview banner on
 * the one page whose whole job is to be read and believed.
 *
 * The ledger entry is L25, "Legal reading, TOC and explicit consent states": "Restore reading
 * template, version changes and legal deep-links. Anonymous reading stays available; real consent
 * success needs a receipt."
 *
 * **This container fetches nothing, and that is correct rather than lazy.** `Legal.jsx` imports
 * `shared/legal-source.json` directly — the ONE versioned legal source that the Astro marketing
 * site and this app both read, so the version a family is shown is the version a consent receipt
 * names (`consent.service.js`, F-07). There is no document endpoint to call and there should not
 * be one: a second copy is the drift F-07 removed. So "anonymous reading stays available" is
 * satisfied by the route existing OUTSIDE fixture mode, which is exactly what this adds.
 *
 * **`showReconsent={false}`, deliberately, and it is one line to undo.**
 *
 * `Legal.jsx` offers an Accept control, and nothing on the server can record what it would accept.
 * `consent.service.js` exports `recordConsent`, but it REQUIRES a transaction client and is called
 * only inside the signup bootstrap; no HTTP route records a consent. That service's own comment
 * says so and names the owner of the missing work: *"The re-consent UI (issue #275) will compare
 * this against the user's latest consent_records entry. Nothing compares it today."*
 *
 * So wiring the page live with the button still showing would turn an honest "preview only" into a
 * button that silently does nothing. L25's own words are the test: "real consent success needs a
 * receipt". A control that cannot produce one does not belong on a live page.
 *
 * Reading is delivered here. Re-consent is #275. Restore the control by deleting the
 * `showReconsent={false}` line once an endpoint exists to receipt it.
 */
import React, { Suspense, lazy } from 'react';

import LanternLoader from '../components/LanternLoader.jsx';

const Legal = lazy(() => import('../pages/Legal.jsx'));

export default function LegalRoute() {
  return (
    <Suspense
      fallback={
        <div style={{ padding: '2rem' }}>
          <LanternLoader label="Lighting the lanterns — loading the page." />
        </div>
      }
    >
      <Legal showReconsent={false} />
    </Suspense>
  );
}
